import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, Edit, Lock, Trash2, Grid3x3, Calendar } from 'lucide-react'
import evaluationGridService, { EvaluationGrid } from '../../../services/evaluationGridService'

export function EvaluationGridsListPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const { data: grids, isLoading } = useQuery({
    queryKey: ['evaluationGrids'],
    queryFn: () => evaluationGridService.getAll()
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => evaluationGridService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evaluationGrids'] })
      setDeletingId(null)
    }
  })

  const lockMutation = useMutation({
    mutationFn: (id: string) => evaluationGridService.lock(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evaluationGrids'] })
    }
  })

  const handleDelete = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette grille ?')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/modules/evaluation')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour au module</span>
            </button>
            <button
              onClick={() => navigate('/modules/evaluation/grids/new')}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
            >
              <Plus className="w-5 h-5" />
              <span>Nouvelle grille</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Grilles d'évaluation</h1>
          <p className="text-gray-600">Gérez vos grilles d'évaluation comportementale personnalisées</p>
        </div>

        {/* Grid List */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
          </div>
        ) : !grids || grids.length === 0 ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-violet-100 mb-4">
              <Grid3x3 className="w-8 h-8 text-violet-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Aucune grille créée</h3>
            <p className="text-gray-600 mb-6">Commencez par créer votre première grille d'évaluation</p>
            <button
              onClick={() => navigate('/modules/evaluation/grids/new')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
            >
              <Plus className="w-5 h-5" />
              <span>Créer ma première grille</span>
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {grids.map((grid) => (
              <GridCard
                key={grid.id}
                grid={grid}
                onEdit={() => navigate(`/modules/evaluation/grids/${grid.id}/edit`)}
                onLock={() => lockMutation.mutate(grid.id)}
                onDelete={() => handleDelete(grid.id)}
                isDeleting={deletingId === grid.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function GridCard({
  grid,
  onEdit,
  onLock,
  onDelete,
  isDeleting
}: {
  grid: EvaluationGrid
  onEdit: () => void
  onLock: () => void
  onDelete: () => void
  isDeleting: boolean
}) {
  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all group">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-bold text-gray-900 mb-1">{grid.name}</h3>
          <p className="text-sm text-gray-600">{grid.className || 'Classe inconnue'}</p>
        </div>
        {grid.isLocked && (
          <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-md text-xs font-medium">
            <Lock className="w-3 h-3" />
            <span>Verrouillée</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 text-sm text-gray-600 mb-4">
        <div className="flex items-center gap-1">
          <Calendar className="w-4 h-4" />
          <span>{grid.academicYear}</span>
        </div>
        <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded-md font-medium">
          {grid.semester}
        </span>
      </div>

      <div className="mb-4">
        <p className="text-sm text-gray-600">
          <span className="font-semibold">{grid.criteria?.length || 0}</span> critère{(grid.criteria?.length || 0) > 1 ? 's' : ''}
        </p>
      </div>

      <div className="flex gap-2 pt-4 border-t border-gray-200">
        <button
          onClick={onEdit}
          disabled={grid.isLocked}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-violet-50 text-violet-700 rounded-lg hover:bg-violet-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Edit className="w-4 h-4" />
          <span className="text-sm font-medium">Éditer</span>
        </button>
        {!grid.isLocked && (
          <button
            onClick={onLock}
            className="flex items-center justify-center px-3 py-2 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors"
            title="Verrouiller la grille"
          >
            <Lock className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={onDelete}
          disabled={isDeleting || grid.isLocked}
          className="flex items-center justify-center px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Supprimer la grille"
        >
          {isDeleting ? (
            <div className="w-4 h-4 border-2 border-red-700 border-t-transparent rounded-full animate-spin" />
          ) : (
            <Trash2 className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  )
}
